<template>
  <section class="date-wise-report">
    <b-card no-body class="report-card mb-2">
      <b-card-body>
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
          <div>
            <h4 class="mb-0">Date Wise Report</h4>
            <small class="text-muted">Filter invoices by date, status, and payment method</small>
          </div>
          <div class="d-flex flex-wrap align-items-center report-actions">
            <b-form-input
              v-model="searchTerm"
              class="report-search report-search--compact mr-1 mb-50"
              placeholder="Search invoice, user, mobile..."
            />
            <b-button
              variant="outline-primary"
              class="mr-1 mb-50"
              :disabled="isLoading || !tableRows.length"
              @click="exportCsv"
            >
              <feather-icon icon="DownloadIcon" size="16" class="mr-50" />
              Export CSV
            </b-button>
            <b-button variant="primary" class="mb-50" :disabled="isLoading" @click="applyFilters">
              Apply
            </b-button>
          </div>
        </div>

        <b-row class="mb-1">
          <b-col
            v-for="item in summaryCards"
            :key="item.label"
            xl="3"
            sm="6"
            class="mb-50"
          >
            <div class="d-flex align-items-center report-kpi-card">
              <b-avatar size="36" :variant="item.color" class="mr-1">
                <feather-icon :icon="item.icon" size="18" />
              </b-avatar>
              <div>
                <div class="font-weight-bolder">{{ item.value }}</div>
                <small class="text-muted">{{ item.label }}</small>
              </div>
            </div>
          </b-col>
        </b-row>

        <div class="report-filter-panel mb-1">
          <b-row>
            <b-col md="3">
              <b-form-group label="From">
                <flat-pickr
                  v-model="from_date"
                  :config="dateConfig"
                  class="form-control"
                  placeholder="YYYY-MM-DD"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="To">
                <flat-pickr
                  v-model="to_date"
                  :config="dateConfig"
                  class="form-control"
                  placeholder="YYYY-MM-DD"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Status">
                <b-form-select v-model="payment_status" :options="statusOptions" />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Payment Method">
                <b-form-select v-model="payment_method" :options="methodOptions" />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Date Basis">
                <b-form-select v-model="date_basis" :options="dateBasisOptions" />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="d-flex align-items-center justify-content-between mt-0 quick-range-row">
            <div class="d-flex align-items-center quick-range-list">
              <small class="text-muted mr-50">Quick range:</small>
              <b-badge
                v-for="preset in presetOptions"
                :key="preset.key"
                variant="light-primary"
                :class="['mr-50', 'cursor-pointer', 'report-preset', { 'report-preset--active': activePreset === preset.key }]"
                @click="applyPreset(preset.key)"
              >
                {{ preset.label }}
              </b-badge>
            </div>
            <div class="d-flex align-items-center report-actions-right">
              <b-form-checkbox
                v-model="include_ppp"
                switch
                class="report-ppp-toggle mr-1"
                :disabled="payment_status !== '0'"
              >
                Show MikroTik Secret Info (Unpaid only)
              </b-form-checkbox>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-1"
                @click="resetFilters"
              >
                Reset
              </b-button>
              <b-button
                size="sm"
                variant="primary"
                @click="applyFilters"
              >
                Refresh
              </b-button>
            </div>
          </div>
        </div>

      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-body>
        <div v-if="isLoading" class="report-skeleton">
          <div v-for="n in 5" :key="`report-skeleton-${n}`" class="report-skeleton-row">
            <div class="report-skeleton-block report-skeleton-block--lg"></div>
            <div class="report-skeleton-block"></div>
            <div class="report-skeleton-block"></div>
            <div class="report-skeleton-block"></div>
            <div class="report-skeleton-block report-skeleton-block--sm"></div>
          </div>
        </div>
        <b-alert v-else-if="loadError" variant="danger" show>
          {{ loadError }}
        </b-alert>
        <vue-good-table
          v-else
          :columns="columns"
          :rows="tableRows"
          :line-numbers="true"
          :rtl="direction"
          style-class="vgt-table condensed datewise-table"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm,
          }"
          :pagination-options="{
            enabled: true,
            perPage: pageLength,
            perPageDropdown: pageOptions,
            dropdownAllowAll: false,
            position: 'bottom',
          }"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'amount'" class="font-weight-bolder text-dark text-right d-block">
              {{ formatAmount(props.row.amount) }}
            </span>
            <span v-else-if="props.column.field === 'paid_date'">
              {{ formatDateValue(props.row.paid_date) }}
            </span>
            <span v-else-if="props.column.field === 'invoice_date'">
              {{ formatDateValue(props.row.invoice_date) }}
            </span>
            <span v-else-if="props.column.field === 'last_payment_date'">
              {{ formatDateValue(props.row.last_payment_date) }}
            </span>
            <span v-else-if="props.column.field === 'status'">
              <b-badge :variant="props.row.status === 'Paid' ? 'light-success' : 'light-danger'">
                {{ props.row.status }}
              </b-badge>
            </span>
            <span v-else-if="props.column.field === 'invoice_code'">
              <router-link
                v-if="props.row.id"
                :to="{ name: 'isp-invoice-preview', params: { invoiceId: props.row.id } }"
                target="_blank"
                class="font-weight-bold"
                :title="`User: ${props.row.isp_user_name || '-'} | Amount: ${formatAmount(props.row.amount)}`"
              >
                {{ props.row.invoice_code }}
              </router-link>
              <span
                v-else
                :title="`User: ${props.row.isp_user_name || '-'} | Amount: ${formatAmount(props.row.amount)}`"
              >
                {{ props.row.invoice_code }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'payment_method'">
              <b-badge :variant="paymentMethodVariant(props.row.payment_method)" class="text-capitalize">
                {{ formatPaymentMethod(props.row.payment_method) }}
              </b-badge>
            </span>
            <span v-else-if="props.column.field === 'payment_note'">
              <span
                class="payment-note-cell"
                :title="props.row.payment_note || ''"
              >
                {{ props.row.payment_note || "-" }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'ppp_active'">
              <b-badge :variant="props.row.ppp_active === true ? 'light-success' : props.row.ppp_active === false ? 'light-danger' : 'light-secondary'">
                {{ props.row.ppp_active === true ? "Active" : props.row.ppp_active === false ? "Inactive" : "N/A" }}
              </b-badge>
            </span>
            <span v-else-if="props.column.field === 'ppp_secret_disabled'">
              <b-badge :variant="props.row.ppp_secret_disabled === true ? 'light-danger' : props.row.ppp_secret_disabled === false ? 'light-success' : 'light-secondary'">
                {{ props.row.ppp_secret_disabled === true ? "Disabled" : props.row.ppp_secret_disabled === false ? "Enabled" : "N/A" }}
              </b-badge>
            </span>
            <span v-else-if="props.column.field === 'ppp_last_logged_out'">
              {{ formatDateValue(props.row.ppp_last_logged_out) }}
            </span>
            <span v-else-if="props.column.field === 'last_app_hit'">
              <span class="mono-compact">{{ props.row.last_app_hit || "-" }}</span>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>

        <div v-if="!isLoading && !loadError && tableRows.length === 0" class="text-center text-muted my-2">
          No invoices found. Adjust the date range or status and try again.
        </div>
      </b-card-body>
    </b-card>
  </section>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import flatPickr from "vue-flatpickr-component";
import store from "@/store";
import Mixin from "@core/services/mixin";
import { DATE_WISE_REPORT } from "@core/services/api";

export default {
  mixins: [Mixin],
  components: {
    BAlert,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    VueGoodTable,
    flatPickr,
  },
  data() {
    return {
      ispUser: JSON.parse(localStorage.getItem("userData")) || {},
      invoices: [],
      summary: {
        total_invoice: 0,
        total_amount: 0,
      },
      from_date: "",
      to_date: "",
      payment_status: "all",
      payment_method: "0",
      date_basis: "invoice_date",
      include_ppp: false,
      pageLength: 500,
      pageOptions: [500, 1000, 2000],
      searchTerm: "",
      isLoading: false,
      loadError: "",
      dateConfig: {
        dateFormat: "Y-m-d",
      },
      presetOptions: [
        { key: "today", label: "Today" },
        { key: "last7", label: "Last 7 Days" },
        { key: "month", label: "This Month" },
        { key: "lastMonth", label: "Last Month" },
        { key: "minus2", label: "2 Months Ago" },
        { key: "minus3", label: "3 Months Ago" },
      ],
      activePreset: "last7",
      suppressAutoFetch: false,
    };
  },
  computed: {
    direction() {
      return store.state.appConfig.isRTL;
    },
    ispId() {
      return this.ispUser?.isp_id || this.ispUser?.id || null;
    },
    statusOptions() {
      return [
        { value: "all", text: "All" },
        { value: "1", text: "Paid" },
        { value: "0", text: "Unpaid" },
      ];
    },
    methodOptions() {
      return [
        { value: "0", text: "All" },
        { value: "1", text: "Cash" },
        { value: "2", text: "Gateway" },
      ];
    },
    dateBasisOptions() {
      const paidDisabled = this.payment_status === "0";
      return [
        { value: "invoice_date", text: "Invoice Date" },
        { value: "paid_date", text: "Paid Date", disabled: paidDisabled },
      ];
    },
    columns() {
      const cols = [
        { label: "Invoice No.", field: "invoice_code" },
        { label: "Username", field: "isp_user_name" },
        { label: "Phone", field: "mobile" },
        { label: "Amount (TK)", field: "amount" },
        { label: "Payment Status", field: "status" },
        { label: "Method", field: "payment_method" },
        { label: "Note", field: "payment_note" },
        { label: "Bill Date", field: "invoice_date" },
        { label: "Payment Date", field: "paid_date" },
      ];

      if (this.payment_status === "0") {
        cols.push({ label: "Last Payment Date", field: "last_payment_date" });
      }

      cols.push(
        ...(this.include_ppp ? [
          { label: "MikroTik User", field: "ppp_active" },
          { label: "MikroTik Secret", field: "ppp_secret_disabled" },
          { label: "MikroTik Last Logout", field: "ppp_last_logged_out" },
        ] : []),
        { label: "Last App Activity", field: "last_app_hit" }
      );

      return cols;
    },
    tableRows() {
      return this.invoices.map((invoice) => ({
        ...invoice,
        status: this.resolveStatus(invoice),
        mobile: this.formatMobile(invoice.mobile),
      }));
    },
    summaryCards() {
      const total = this.summary.total_invoice || this.invoices.length;
      const totalAmount = this.summary.total_amount || this.totalAmount(this.invoices);
      const paidCount = this.invoices.filter((item) => this.resolveStatus(item) === "Paid").length;
      const unpaidCount = this.invoices.filter((item) => this.resolveStatus(item) === "Unpaid").length;

      return [
        { label: "Total Invoices", value: total, icon: "FileTextIcon", color: "light-primary" },
        { label: "Paid", value: paidCount, icon: "CheckCircleIcon", color: "light-success" },
        { label: "Unpaid", value: unpaidCount, icon: "XCircleIcon", color: "light-danger" },
        { label: "Total Amount", value: this.formatAmount(totalAmount), icon: "DollarSignIcon", color: "light-info" },
      ];
    },
  },
  watch: {
    payment_status(value) {
      if (value !== "0") {
        this.include_ppp = false;
      }
      if (value === "0" && this.date_basis === "paid_date") {
        this.suppressAutoFetch = true;
        this.date_basis = "invoice_date";
        this.suppressAutoFetch = false;
      }
      if (!this.suppressAutoFetch) {
        this.fetchReport();
      }
    },
    payment_method() {
      if (!this.suppressAutoFetch) {
        this.fetchReport();
      }
    },
    date_basis() {
      if (!this.suppressAutoFetch) {
        this.fetchReport();
      }
    },
  },
  mounted() {
    this.applyPreset("last7");
  },
  methods: {
    formatAmount(value) {
      const amount = Number(value);
      if (Number.isNaN(amount)) return value;
      const formatted = amount % 1 === 0
        ? String(Math.trunc(amount))
        : amount.toFixed(2).replace(/\.?0+$/, "");
      return `${formatted} TK`;
    },
    formatMobile(value) {
      if (!value) return "-";
      const text = String(value);
      return text.startsWith("+") ? text : `+${text}`;
    },
    formatPaymentMethod(value) {
      if (value === 1 || value === "1") return "Cash";
      if (value === 2 || value === "2") return "Gateway";
      return "N/A";
    },
    paymentMethodVariant(value) {
      if (value === 1 || value === "1") return "light-info";
      if (value === 2 || value === "2") return "light-primary";
      return "light-secondary";
    },
    totalAmount(items) {
      return items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    },
    formatDateValue(value) {
      if (!value) return "N/A";
      return value;
    },
    resolveStatus(invoice) {
      if (invoice && invoice.payment_status !== undefined && invoice.payment_status !== null) {
        return Number(invoice.payment_status) === 1 ? "Paid" : "Unpaid";
      }
      return this.payment_status === "1" ? "Paid" : "Unpaid";
    },
    applyPreset(key, shouldFetch = true) {
      const today = new Date();
      this.activePreset = key;
      if (key === "today") {
        this.from_date = this.formatDate(today);
        this.to_date = this.formatDate(today);
        if (shouldFetch) this.fetchReport();
        return;
      }
      if (key === "last7") {
        const start = new Date();
        start.setDate(today.getDate() - 6);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(today);
        if (shouldFetch) this.fetchReport();
        return;
      }
      if (key === "month") {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(today);
        if (shouldFetch) this.fetchReport();
        return;
      }
      if (key === "lastMonth") {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(end);
        if (shouldFetch) this.fetchReport();
        return;
      }
      if (key === "minus2") {
        const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        const end = new Date(today.getFullYear(), today.getMonth() - 1, 0);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(end);
        if (shouldFetch) this.fetchReport();
        return;
      }
      if (key === "minus3") {
        const start = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        const end = new Date(today.getFullYear(), today.getMonth() - 2, 0);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(end);
        if (shouldFetch) this.fetchReport();
      }
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    resetFilters() {
      this.suppressAutoFetch = true;
      this.payment_status = "1";
      this.payment_method = "0";
      this.date_basis = "invoice_date";
      this.include_ppp = false;
      this.suppressAutoFetch = false;
      this.applyPreset("last7", false);
      this.fetchReport();
    },
    applyFilters() {
      this.fetchReport();
    },
    fetchReport() {
      if (!this.ispId) {
        this.loadError = "Unable to detect ISP.";
        this.invoices = [];
        return;
      }
      this.isLoading = true;
      this.loadError = "";
      const params = {
        isp_id: this.ispId,
        from_date: this.from_date,
        to_date: this.to_date,
        payment_status: this.payment_status,
        payment_method: this.payment_method,
        date_basis: this.date_basis,
        include_ppp: this.include_ppp ? 1 : 0,
      };
      this.$http
        .get(process.env.VUE_APP_BASEURL + DATE_WISE_REPORT, { params })
        .then((res) => {
          const payload = res.data && res.data.data ? res.data.data : {};
          this.invoices = payload.invoices || [];
          this.summary = {
            total_invoice: payload.total_invoice || this.invoices.length,
            total_amount: payload.total || this.totalAmount(this.invoices),
          };
        })
        .catch(() => {
          this.loadError = "Failed to load report. Please try again.";
          this.invoices = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    exportCsv() {
      const headers = [
        "Paid Date",
        "Invoice Date",
        "Last Payment Date",
        "Invoice ID",
        "User",
        "Mobile",
        "Amount",
        "Payment Method",
        "Payment Note",
        ...(this.include_ppp ? ["PPP Active", "PPP Secret", "PPP Last Logout"] : []),
        "Status",
        "Last App Login",
      ];
      const rows = this.tableRows.map((row) => [
        this.formatDateValue(row.paid_date),
        this.formatDateValue(row.invoice_date),
        this.formatDateValue(row.last_payment_date),
        row.invoice_code,
        row.isp_user_name,
        row.mobile,
        row.amount,
        this.formatPaymentMethod(row.payment_method),
        row.payment_note || "",
        ...(this.include_ppp ? [
          row.ppp_active === true ? "Active" : row.ppp_active === false ? "Inactive" : "N/A",
          row.ppp_secret_disabled === true ? "Disabled" : row.ppp_secret_disabled === false ? "Enabled" : "N/A",
          this.formatDateValue(row.ppp_last_logged_out),
        ] : []),
        row.status,
        row.last_app_hit || "-",
      ]);
      const csv = [headers, ...rows].map((line) => line.map(this.escapeCsv).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `date-wise-report-${this.from_date || "all"}-${this.to_date || "all"}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    escapeCsv(value) {
      const text = String(value ?? "");
      if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, "\"\"")}"`;
      }
      return text;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.report-card {
  .report-kpi-card {
    background: #f8fafc;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 0.6rem;
    padding: 0.5rem 0.75rem;
    min-height: 58px;
  }

  .report-filter-panel {
    background: #fbfbfc;
    border: 1px solid #e6e8eb;
    border-radius: 0.6rem;
    padding: 0.5rem 0.75rem;
  }

  .report-search {
    max-width: 320px;
  }

  .report-search--compact {
    width: 240px;
    flex: 0 0 240px;
  }
}

.report-card .report-actions .btn {
  padding: 0.4rem 0.75rem;
}
.report-card .report-actions .form-control {
  height: 36px;
}
.report-filter-panel .form-group {
  margin-bottom: 0.5rem;
}
.report-filter-panel .form-control,
.report-filter-panel .custom-select {
  height: 36px;
  font-size: 0.85rem;
}
.quick-range-row {
  gap: 0.75rem;
  flex-wrap: nowrap;
}
.quick-range-list {
  gap: 0.5rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
}
.report-actions-right {
  gap: 0.5rem;
  flex-wrap: nowrap;
}
.report-ppp-toggle {
  margin-bottom: 0;
  white-space: nowrap;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  border: 1px solid #c7d2fe;
  background: #eef2ff;
  display: inline-flex;
  align-items: center;
}
.report-ppp-toggle .custom-control-label {
  font-size: 0.8rem;
  color: #4338ca;
  font-weight: 600;
}
.report-ppp-toggle .custom-control-input:disabled ~ .custom-control-label {
  color: #94a3b8;
}
.report-ppp-toggle .custom-control-input:disabled ~ .custom-control-label::before {
  border-color: #e2e8f0;
  background: #f8fafc;
}
.report-ppp-toggle .custom-control-input:checked ~ .custom-control-label::before {
  border-color: #4f46e5;
  background: #4f46e5;
}
.report-ppp-toggle .custom-control-label::before {
  border-color: #a5b4fc;
}
.quick-range-list::-webkit-scrollbar {
  height: 4px;
}
.quick-range-list::-webkit-scrollbar-thumb {
  background: #d7dbe3;
  border-radius: 999px;
}

@media (min-width: 992px) {
  .report-actions {
    flex-wrap: nowrap;
  }
}

@media (max-width: 576px) {
  .report-search--compact {
    width: 100%;
    flex: 1 0 100%;
  }
}
@media (max-width: 992px) {
  .quick-range-row {
    flex-wrap: wrap;
  }
  .report-actions-right {
    width: 100%;
    justify-content: flex-start;
  }
}

.report-skeleton {
  display: grid;
  gap: 0.75rem;
}

.report-skeleton-row {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(120px, 1fr) minmax(140px, 0.9fr) minmax(140px, 0.9fr) minmax(80px, 0.6fr);
  gap: 0.75rem;
}

.report-skeleton-block {
  height: 12px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eceff3 0%, #f7f8fa 50%, #eceff3 100%);
  background-size: 200% 100%;
  animation: report-skeleton 1.4s ease-in-out infinite;
}

.report-skeleton-block--lg {
  height: 14px;
}

.report-skeleton-block--sm {
  max-width: 120px;
}

.report-preset {
  border: 1px solid #d7dbe3;
  background: #f3f6fb;
  color: #3b4252;
  padding: 0.15rem 0.5rem;
  font-size: 0.75rem;
  transition: all 0.2s ease;
}

.report-preset:hover {
  background: #e8efff;
}

.report-preset--active {
  background: #2f5bff;
  border-color: #2f5bff;
  color: #fff;
  box-shadow: 0 6px 16px rgba(47, 91, 255, 0.24);
}

.payment-note-cell {
  display: inline-block;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

.datewise-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 2;
}
.datewise-table th,
.datewise-table td {
  padding: 0.55rem 0.75rem;
  vertical-align: middle;
  white-space: nowrap;
}
.datewise-table tr:hover {
  background: #f9fafb;
}
.datewise-table .vgt-left-align {
  text-align: left;
}
.datewise-table .vgt-right-align {
  text-align: right;
}
.datewise-table .vgt-center-align {
  text-align: center;
}
.datewise-table td:nth-child(4) {
  font-weight: 600;
}
.datewise-table td:nth-child(10),
.datewise-table td:nth-child(11) {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.86rem;
  color: #6b7280;
}
.mono-compact {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.86rem;
  color: #6b7280;
}

@keyframes report-skeleton {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
